// Route map. Public login screens + an authenticated area (inside the role-scoped app shell).
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore.js';
import Login from './pages/Login.jsx';
import VerifyOtp from './pages/VerifyOtp.jsx';
import Register from './pages/Register.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import AppShell from './components/shell/AppShell.jsx';
import RoleHome from './pages/RoleHome.jsx';
import ClientsIndex from './pages/ClientsIndex.jsx';
import ClientTimeline from './pages/ClientTimeline.jsx';
import CatalogRoute from './pages/CatalogRoute.jsx';
import Orders from './pages/Orders.jsx';
import OrderDetail from './pages/OrderDetail.jsx';
import ClientInvoices from './pages/ClientInvoices.jsx';
import ClientPromotions from './pages/ClientPromotions.jsx';
import Lookbooks from './pages/Lookbooks.jsx';
import Messages from './pages/Messages.jsx';
import TeamMember from './pages/TeamMember.jsx';
import Commissions from './pages/Commissions.jsx';
import Receivables from './pages/Receivables.jsx';
import Discounts from './pages/Discounts.jsx';
import Marketing from './pages/Marketing.jsx';
import MarketingCampaign from './pages/MarketingCampaign.jsx';
import ClientHomePreview from './pages/ClientHomePreview.jsx';
import Unsubscribe from './pages/Unsubscribe.jsx';
import Payments from './pages/Payments.jsx';
import Settings from './pages/Settings.jsx';
import Account from './pages/Account.jsx';

const STAFF = ['INTERNAL_STAFF'];
const STAFF_REP = ['INTERNAL_STAFF', 'REP'];
// Order entry: clients (self), reps (assigned clients), and staff (any client).
const ORDER_ROLES = ['INTERNAL_STAFF', 'REP', 'CLIENT'];

export default function App() {
  const init = useAuthStore((s) => s.init);
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <Routes>
      <Route
        path="/login"
        element={status === 'authenticated' ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/login/verify"
        element={status === 'authenticated' ? <Navigate to="/" replace /> : <VerifyOtp />}
      />
      <Route
        path="/register"
        element={status === 'authenticated' ? <Navigate to="/" replace /> : <Register />}
      />

      {/* Public: unsubscribe link from marketing emails (signed token, no login). */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />

      {/* Authenticated app — everything below renders inside the role-scoped shell. */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleHome />} />

        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route
          path="/catalog"
          element={
            <RoleRoute roles={ORDER_ROLES}>
              <CatalogRoute />
            </RoleRoute>
          }
        />
        {/* Legacy route: this shipped as /catalog. Kept as a redirect so any old bookmark or emailed
            link lands on the real order builder instead of a "coming in Phase 2" placeholder. */}
        <Route path="/new-order" element={<Navigate to="/catalog" replace />} />
        <Route
          path="/clients"
          element={
            <RoleRoute roles={STAFF_REP}>
              <ClientsIndex />
            </RoleRoute>
          }
        />
        <Route
          path="/clients/:companyId"
          element={
            <RoleRoute roles={STAFF}>
              <ClientTimeline />
            </RoleRoute>
          }
        />
        <Route
          path="/clients/:companyId/home"
          element={
            <RoleRoute roles={STAFF_REP}>
              <ClientHomePreview />
            </RoleRoute>
          }
        />
        <Route
          path="/lookbooks"
          element={
            <RoleRoute roles={STAFF}>
              <Lookbooks />
            </RoleRoute>
          }
        />
        {/* Marketing emails — admins + managers (enforced server-side; nav hides it from others). */}
        <Route
          path="/marketing"
          element={
            <RoleRoute roles={STAFF}>
              <Marketing />
            </RoleRoute>
          }
        />
        <Route
          path="/marketing/:id"
          element={
            <RoleRoute roles={STAFF}>
              <MarketingCampaign />
            </RoleRoute>
          }
        />
        <Route
          path="/discounts"
          element={
            <RoleRoute roles={STAFF}>
              <Discounts />
            </RoleRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <RoleRoute roles={STAFF_REP}>
              <Payments />
            </RoleRoute>
          }
        />
        {/* Commissions — admins manage/pay; reps see their own (page branches by capability). */}
        <Route
          path="/commissions"
          element={
            <RoleRoute roles={STAFF_REP}>
              <Commissions />
            </RoleRoute>
          }
        />
        {/* Receivables & collections — admins + managers (payment authority; enforced server-side). */}
        <Route
          path="/receivables"
          element={
            <RoleRoute roles={STAFF}>
              <Receivables />
            </RoleRoute>
          }
        />
        {/* Messages — available to every role (clients/reps ↔ internal staff). */}
        <Route path="/messages" element={<Messages />} />
        {/* Team-member dashboard — admins view anyone; a member views their own (backend-enforced). */}
        <Route
          path="/team/:userId"
          element={
            <RoleRoute roles={STAFF_REP}>
              <TeamMember />
            </RoleRoute>
          }
        />
        {/* Analytics is merged into the home Insights module. */}
        <Route path="/analytics" element={<Navigate to="/" replace />} />
        <Route
          path="/invoices"
          element={
            <RoleRoute roles={['CLIENT']}>
              <ClientInvoices />
            </RoleRoute>
          }
        />
        <Route
          path="/promotions"
          element={
            <RoleRoute roles={['CLIENT']}>
              <ClientPromotions />
            </RoleRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <RoleRoute roles={STAFF}>
              <Settings />
            </RoleRoute>
          }
        />
        {/* Account is available to every authenticated user. */}
        <Route path="/account" element={<Account />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
